import { TabArraySchema, TabSchema } from '@dat/iq-control-tab';
import { z } from 'zod';
import { Control, OptionSchemaValue } from '../../models';

const dashboardControlsBasePayloadSchema = z.object({
    dashboardControlId: z.string(),
});

const controlBasePayloadSchema = dashboardControlsBasePayloadSchema.merge(
    z.object({
        controlName: z.string(),
    }),
);

const optionBasePayloadSchema = controlBasePayloadSchema.merge(
    z.object({
        optionValue: z.string(),
    }),
);

const controlActionBasePayloadSchema = z.object({
    dashboardControlId: z.string(),
    controlName: z.string().optional(),
    optionValue: z.string().optional(),
});

export const InputValueTypeSchema = z.union([
    z.number(),
    z.boolean(),
    z.string(),
    OptionSchemaValue,
    TabSchema,
]);

export type InputValueTypes = z.infer<typeof InputValueTypeSchema>;

export const EnableControlPayloadSchema = controlBasePayloadSchema;
export type EnableControlPayload = Required<
    z.infer<typeof EnableControlPayloadSchema>
>;

export const DisableControlPayloadSchema = controlBasePayloadSchema;
export type DisableControlPayload = Required<
    z.infer<typeof DisableControlPayloadSchema>
>;

export const SetControlValuePayloadSchema = controlBasePayloadSchema.merge(
    z.object({
        value: InputValueTypeSchema,
    }),
);
export type SetControlValuePayload = Required<
    z.infer<typeof SetControlValuePayloadSchema>
>;

export const SetDashboardControlsValuePayloadSchema =
    dashboardControlsBasePayloadSchema.merge(
        z.object({
            value: z.record(z.string(), InputValueTypeSchema),
        }),
    );
export type SetDashboardControlsValuePayload = Required<
    z.infer<typeof SetDashboardControlsValuePayloadSchema>
>;
export const SetControlPropPayloadSchema = controlBasePayloadSchema.merge(
    z.object({
        props: z.record(z.string(), z.any()),
    }),
);
export type SetControlPropPayload = Required<
    z.infer<typeof SetControlPropPayloadSchema>
>;
export const PatchDashboardControlsValuePayloadSchema =
    dashboardControlsBasePayloadSchema.merge(
        z.object({
            value: z.record(z.string(), InputValueTypeSchema),
        }),
    );
export type PatchDashboardControlsValuePayload = Required<
    z.infer<typeof PatchDashboardControlsValuePayloadSchema>
>;

/**
 * Payload schema for the `setValue` action
 */
export const setValuePayloadSchema = controlActionBasePayloadSchema.merge(
    z.object({
        value: z.any(),
    }),
);
export function isSetValuePayload(payload: any): payload is SetValuePayload {
    return setValuePayloadSchema.safeParse(payload).success;
}
export type SetValuePayload = z.infer<typeof setValuePayloadSchema>;

/**
 * Payload schema for the `patchValue` action
 */
export const patchValuePayloadSchema = controlActionBasePayloadSchema.merge(
    z.object({
        value: z.any(),
    }),
);
export function isPatchValuePayload(
    payload: any,
): payload is PatchValuePayload {
    return patchValuePayloadSchema.safeParse(payload).success;
}
export type PatchValuePayload = z.infer<typeof patchValuePayloadSchema>;

/**
 * Payload schema for the `setDisabled` action
 */
export const setDisabledPayloadSchema = controlActionBasePayloadSchema.merge(
    z.object({
        disabled: z.boolean(),
    }),
);
export function isSetDisabledPayload(
    payload: any,
): payload is SetDisabledPayload {
    return setDisabledPayloadSchema.safeParse(payload).success;
}
export type SetDisabledPayload = z.infer<typeof setDisabledPayloadSchema>;

export const validatorSchema = z.object({
    minValue: z.number().optional(),
    maxValue: z.number().optional(),
    required: z.boolean().optional(),
});
export type Validators = z.infer<typeof validatorSchema>;

/**
 * Payload schema for the `setValidators` action
 */
export const setValidatorsPayloadSchema = controlActionBasePayloadSchema.merge(
    z.object({
        validators: z.custom<Validators>(),
    }),
);
export function isSetValidatorsPayload(
    payload: any,
): payload is SetValidatorsPayload {
    return setValidatorsPayloadSchema.safeParse(payload).success;
}
export type SetValidatorsPayload = z.infer<typeof setValidatorsPayloadSchema>;

/**
 * Payload schema for the `reset` action
 */
export const resetPayloadSchema = controlActionBasePayloadSchema;
export function isResetPayload(payload: any): payload is ResetPayload {
    return resetPayloadSchema.safeParse(payload).success;
}
export type ResetPayload = z.infer<typeof resetPayloadSchema>;

export const UpdateTabsPayloadSchema = controlActionBasePayloadSchema.merge(
    z.object({
        tabs: TabArraySchema,
        value: z.object({value: z.string()}).optional(),
    }),
);

export type UpdateTabsPayload = z.infer<typeof UpdateTabsPayloadSchema>;
export const UpdateLabelPayloadSchema = controlActionBasePayloadSchema.merge(
    z.object({
        label: z.string().optional(),
        checkedLabel: z.string().optional(),
        uncheckedLabel: z.string().optional(),
    }),
);

export type UpdateLabelPayload = z.infer<typeof UpdateLabelPayloadSchema>;

/**
 * Payload schema for the `submit` action
 */
export const submitPayloadSchema = controlActionBasePayloadSchema;
export function isSubmitPayload(payload: any): payload is SubmitPayload {
    return submitPayloadSchema.safeParse(payload).success;
}
export type SubmitPayload = z.infer<typeof submitPayloadSchema>;

/**
 * Payload schema for the `updateProps` action
 */
export const updatePropsSchema = controlActionBasePayloadSchema.merge(
    z.object({
        // controlUpdates: z.any(),
        controlUpdates: z.array(z.custom<Control>()),
        onSubmit: z.array(z.any()).optional(),
        // TODO: don't think options are needed here?
        option: z.any().optional(),
    }),
);
export function isUpdatePropsPayload(
    payload: any,
): payload is UpdatePropsPayload {
    return updatePropsSchema.safeParse(payload).success;
}
export type UpdatePropsPayload = z.infer<typeof updatePropsSchema>;

/**
 * Payload schema for the `removeControl` action
 */
export const removeControlPayloadSchema = z.object({
    dashboardControlId: z.string(),
    controlNames: z.array(z.string()),
});
export function isRemoveControlPayload(
    payload: any,
): payload is RemoveControlPayload {
    return removeControlPayloadSchema.safeParse(payload).success;
}
export type RemoveControlPayload = z.infer<typeof removeControlPayloadSchema>;

/**
 * Payload schema for the `addControl` action
 */
export const AddControlPayloadSchema = dashboardControlsBasePayloadSchema.merge(
    z.object({
        control: z.any(),
        indexAt: z.number().optional(),
    }),
);

export type AddControlPayload = z.infer<typeof AddControlPayloadSchema>;

/**
 * Payload schema for the `updateFormCache` action
 */
export const UpdateFormCachePayloadSchema = z.object({
    formId: z.string(),
    data: z.any(),
});

export type UpdateFormCachePayload = z.infer<
    typeof UpdateFormCachePayloadSchema
>;
